export interface GameSettings {
  groupedMode: boolean;      // true = root+children, false = solo only
  showExplanation: boolean;  // true = full remediation, false = fast mode
  audioEnabled: boolean;     // audio on/off
  selectedCharacter: string; // e.g., 'knight', 'wizard', etc.
}

export interface SubjectStats {
  encountered: number;
  correct: number;
  wrong: number;
  bestStreak: number;
}

export interface ProfileData {
  version: number;
  createdAt: string;
  lastPlayed: string;
  totalCorrect: number;
  totalWrong: number;
  totalXP: number;
  totalCoins: number;
  cardsEncountered: string[];
  cardsMastered: string[];
  subjectStats: Record<string, SubjectStats>;
  settings: GameSettings;
}

const STORAGE_KEY = 'kim-bardashian-final-chance-profile';
const PROFILE_VERSION = 2;

export class ProfileStore {
  private profile: ProfileData;

  constructor() {
    this.profile = this.load();
  }

  private createDefaultProfile(): ProfileData {
    return {
      version: PROFILE_VERSION,
      createdAt: new Date().toISOString(),
      lastPlayed: new Date().toISOString(),
      totalCorrect: 0,
      totalWrong: 0,
      totalXP: 0,
      totalCoins: 0,
      cardsEncountered: [],
      cardsMastered: [],
      subjectStats: {},
      settings: {
        groupedMode: true,
        showExplanation: true,
        audioEnabled: true,
        selectedCharacter: 'kim',
      },
    };
  }

  private load(): ProfileData {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Migrate from v1 if needed
        if (parsed.version === 1) {
          return this.migrateFromV1(parsed);
        }
        if (parsed.version === PROFILE_VERSION) {
          // Ensure all settings fields exist (handles profiles saved before new fields were added)
          const defaults = this.createDefaultProfile();
          return {
            ...defaults,
            ...parsed,
            settings: {
              ...defaults.settings,
              ...parsed.settings,
            },
          };
        }
      }
    } catch {
      console.warn('Failed to load profile, creating new one');
    }
    return this.createDefaultProfile();
  }

  private migrateFromV1(oldProfile: any): ProfileData {
    const newProfile = this.createDefaultProfile();
    newProfile.totalCorrect = oldProfile.totalCorrect || 0;
    newProfile.totalWrong = oldProfile.totalWrong || 0;
    newProfile.cardsEncountered = oldProfile.cardsEncountered || [];
    newProfile.cardsMastered = oldProfile.cardsMastered || [];
    newProfile.createdAt = oldProfile.createdAt || new Date().toISOString();
    console.info('Migrated profile from v1 to v2');
    return newProfile;
  }

  private save(): void {
    try {
      this.profile.lastPlayed = new Date().toISOString();
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.profile));
    } catch {
      console.warn('Failed to save profile');
    }
  }

  // XP and Coins
  addXP(amount: number): void {
    this.profile.totalXP += amount;
    this.save();
  }

  addCoins(amount: number): void {
    this.profile.totalCoins += amount;
    this.save();
  }

  spendCoins(amount: number): boolean {
    if (this.profile.totalCoins < amount) return false;
    this.profile.totalCoins -= amount;
    this.save();
    return true;
  }

  getTotalXP(): number {
    return this.profile.totalXP;
  }

  getTotalCoins(): number {
    return this.profile.totalCoins;
  }

  // Subject streaks
  updateSubjectStreak(subject: string, currentStreak: number): void {
    if (!this.profile.subjectStats[subject]) {
      this.profile.subjectStats[subject] = {
        encountered: 0,
        correct: 0,
        wrong: 0,
        bestStreak: 0,
      };
    }
    if (currentStreak > this.profile.subjectStats[subject].bestStreak) {
      this.profile.subjectStats[subject].bestStreak = currentStreak;
    }
    this.save();
  }

  getSubjectStats(subject: string): SubjectStats {
    return this.profile.subjectStats[subject] || {
      encountered: 0,
      correct: 0,
      wrong: 0,
      bestStreak: 0,
    };
  }

  getBestStreak(subject?: string): number {
    if (subject) {
      return this.profile.subjectStats[subject]?.bestStreak || 0;
    }
    // Return overall best streak
    let best = 0;
    for (const stats of Object.values(this.profile.subjectStats)) {
      if (stats.bestStreak > best) {
        best = stats.bestStreak;
      }
    }
    return best;
  }

  // Settings
  getSettings(): GameSettings {
    return { ...this.profile.settings };
  }

  updateSettings(settings: Partial<GameSettings>): void {
    this.profile.settings = { ...this.profile.settings, ...settings };
    this.save();
  }

  isGroupedMode(): boolean {
    return this.profile.settings.groupedMode;
  }

  isShowExplanation(): boolean {
    return this.profile.settings.showExplanation;
  }

  isAudioEnabled(): boolean {
    return this.profile.settings.audioEnabled;
  }

  getSelectedCharacter(): string {
    return this.profile.settings.selectedCharacter || 'kim';
  }

  // Answer tracking
  recordCorrectAnswer(cardId: string): void {
    this.profile.totalCorrect++;
    if (!this.profile.cardsEncountered.includes(cardId)) {
      this.profile.cardsEncountered.push(cardId);
    }
    this.save();
  }

  recordWrongAnswer(cardId: string): void {
    this.profile.totalWrong++;
    if (!this.profile.cardsEncountered.includes(cardId)) {
      this.profile.cardsEncountered.push(cardId);
    }
    this.save();
  }

  markCardMastered(cardId: string): void {
    if (!this.profile.cardsMastered.includes(cardId)) {
      this.profile.cardsMastered.push(cardId);
    }
    this.save();
  }

  getProfile(): ProfileData {
    return { ...this.profile };
  }

  getTotalCorrect(): number {
    return this.profile.totalCorrect;
  }

  getTotalWrong(): number {
    return this.profile.totalWrong;
  }

  getAccuracy(): number {
    const total = this.profile.totalCorrect + this.profile.totalWrong;
    if (total === 0) return 0;
    return Math.round((this.profile.totalCorrect / total) * 100);
  }

  getCardsEncounteredCount(): number {
    return this.profile.cardsEncountered.length;
  }

  getCardsMasteredCount(): number {
    return this.profile.cardsMastered.length;
  }

  reset(): void {
    this.profile = this.createDefaultProfile();
    this.save();
  }
}

// Singleton instance
export const profileStore = new ProfileStore();
